import { useClipboard } from '@calendly/clipboard';
import { Form, SubmitFormButton } from '@calendly/form';
import { useBooleanState, useOnValueChange } from '@calendly/hooks';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconCheck } from '@calendly/ui/components/glyphs/IconCheck';
import { IconCopy } from '@calendly/ui/components/glyphs/IconCopy';
import { IconPencil } from '@calendly/ui/components/glyphs/IconPencil';
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash';
import { IconButton } from '@calendly/ui/components/icon-button';
import { TextInput } from '@calendly/ui/components/text-input';
import { BodyText, HeadlineText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { colorTextAction, colorIconSuccess } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useContext, forwardRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { RichTextField, DirtyFormTracking } from 'components/core/form';
import changeSet from 'core/change_set';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { isCalendlyMeeting } from 'shared/utils';
import { LeaveTabPopup } from '../../leave_tab_popup';
import { MeetingDetailDependenciesContext } from '../../meeting_detail_context';
const NOTES_LENGTH_LIMIT = 25_000;
const notesFormWrapperCss = "nflo4w0";
const NotesFormWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(notesFormWrapperCss, props.className)} />;
});
const notesHeaderCss = "n2ybepk";
const NotesHeader = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(notesHeaderCss, props.className)} />;
});
const notesHeaderRowCss = "n1s709b7";
const NotesHeaderRow = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(notesHeaderRowCss, props.className)} />;
});
const notesBodyCss = "n1pxnz1t";
const NotesBody = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(notesBodyCss, props.className)} />;
});
const actionButtonsCss = "a1xxw3su";
const ActionButtons = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionButtonsCss, props.className)} />;
});
const discardChangesButtonCss = "d1ghsyvr";
const DiscardChangesButton = forwardRef((props, ref) => <Button ref={ref} {...props} className={classNames(discardChangesButtonCss, props.className)} />);
const saveChangesButtonCss = "s1wgdqoi";
const SaveChangesButton = forwardRef((props, ref) => <SubmitFormButton ref={ref} {...props} className={classNames(saveChangesButtonCss, props.className)} />);
const iconsCss = "i1tplf8a";
const Icons = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(iconsCss, props.className)} />;
});
const DEFAULT_VALUE = '<p><br></p>';
const notesLengthValidation = values => {
  if (values.internalNotes > NOTES_LENGTH_LIMIT) {
    return {
      internal_notes: I18n.t('publisher.scheduled_events.meeting_notes.too_long', {
        count: NOTES_LENGTH_LIMIT
      })
    };
  }
  return {};
};
const CopyNoteButton = ({
  notes,
  event,
  location,
  miEventUuid
}) => {
  const textNotes = notes?.replace(/<[^>]*>/g, '');
  const [Wrapper, ref, isCopied] = useClipboard(textNotes || '');
  const track = useTrack();
  useOnValueChange(isCopied, () => {
    if (isCopied) {
      track({
        name: TRACKING_EVENT_NAMES.AI_NOTETAKER_INTERACTION,
        properties: {
          action: 'copy_notes',
          location,
          is_calendly_meeting: isCalendlyMeeting(event),
          event_uuid: event.uuid,
          mi_event_uuid: miEventUuid
        }
      });
    }
  });
  return <Wrapper>
      <IconButton ref={ref}>
        {isCopied ? <UiIcon icon={IconCheck} size="small" color={colorIconSuccess} /> : <UiIcon icon={IconCopy} size="small" color={colorTextAction} />}
      </IconButton>
    </Wrapper>;
};
const MeetingNotesHeader = ({
  showHeaderButtons,
  onEdit,
  notes,
  event,
  location,
  miEventUuid
}) => <NotesHeader>
    <NotesHeaderRow>
      <div>
        <HeadlineText>
          {I18n.t('publisher.meetings.tabs.notes.subheader')}
        </HeadlineText>
        <BodyText color="muted" size="medium">
          {I18n.t('publisher.meetings.tabs.notes.info')}
        </BodyText>
      </div>
      {showHeaderButtons && <Icons>
          <CopyNoteButton notes={notes} event={event} location={location} data-cy="copy-notes-button" miEventUuid={miEventUuid} />
          <IconButton onClick={onEdit} data-cy="edit-notes-button">
            <UiIcon icon={IconPencil} size="small" color={colorTextAction} />
          </IconButton>
        </Icons>}
    </NotesHeaderRow>
  </NotesHeader>;
const HtmlEditor = ({
  showActionButtons,
  onFocus,
  onCancel,
  isLoading
}) => <>
    <RichTextField id="meeting-notes-field" placeholder={I18n.t('publisher.meetings.tabs.notes.placeholder')} name="internal_notes" onFocus={onFocus} autoFocus />
    {showActionButtons && <ActionButtons>
        <DiscardChangesButton decoration="ghost" size="small" onClick={onCancel} disabled={isLoading}>
          <UiIcon icon={IconTrash} size="medium" />
          {I18n.t('publisher.meetings.tabs.notes.discard_changes')}
        </DiscardChangesButton>
        <SaveChangesButton size="small" disabled={isLoading}>
          {I18n.t('publisher.meetings.tabs.notes.save_changes')}
        </SaveChangesButton>
      </ActionButtons>}
  </>;
const MeetingNotesBody = ({
  notes,
  showActionButtons,
  onFocus,
  onCancel,
  onSubmit,
  isLoading,
  editorEnabled,
  onDiscard,
  onClose,
  showLeavePageModal
}) => <NotesBody>
      {!notes && !editorEnabled && <TextInput type="textarea" placeholder={I18n.t('publisher.meetings.tabs.notes.placeholder')} rows={3} onFocus={onFocus} />}
      {notes && !editorEnabled && <div>{HtmlParser(notes)}</div>}
      {editorEnabled && <Form initialValues={{
    internal_notes: {
      html: notes
    }
  }} validate={notesLengthValidation} onSubmit={onSubmit} enableReinitialize>
        <DirtyFormTracking />
        <HtmlEditor showActionButtons={showActionButtons} onFocus={onFocus} onCancel={onCancel} isLoading={isLoading} />
        <LeaveTabPopup isOpen={showLeavePageModal} onClose={onClose} onCancel={onDiscard} onConfirm={onSubmit} isLoading={isLoading} />
      </Form>}
    </NotesBody>;
export const MeetingNotesForm = ({
  event,
  setTempTab,
  tempTab,
  closeLeavePageModal,
  showLeavePageModal,
  switchToTab,
  location,
  miEventUuid
}) => {
  // Get mutation hook from dependencies context
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies?.useUpdateMeetingMutation) {
    throw new Error('MeetingNotesForm must be wrapped in a MeetingDetailDependenciesProvider with useUpdateMeetingMutation');
  }
  const [editorEnabled, enableEditor, disableEditor] = useBooleanState(false);
  const [optimisticNotes, setOptimisticNotes] = useState(null);
  const [updateMeeting, {
    isLoading,
    isSuccess
  }] = dependencies.useUpdateMeetingMutation();
  const history = useHistory();

  // Show submitted value immediately while save is in flight; backend already does cache optimistic update
  const notes = optimisticNotes ?? event.internalNotes;

  // Clear optimistic value once server response is in or when event catches up (e.g. refetch)
  useEffect(() => {
    if (optimisticNotes != null && event.internalNotes === optimisticNotes) {
      setOptimisticNotes(null);
    }
  }, [optimisticNotes, event.internalNotes]);
  const params = new URLSearchParams(history.location.search);
  const track = useTrack();
  const changeTab = () => {
    changeSet.reset();
    switchToTab(tempTab);
    params.set('tab', tempTab);
    setTempTab('');
    history.replace({
      search: params.toString()
    });
  };

  // Only close editor when the selected meeting changes (by uuid), not when the event object
  // reference changes. Cache/merge updates (e.g. list refetch, REST merge, meeting intelligence)
  // can replace activeMeeting with a new object reference; closing on that would dismiss the
  // editor while the user is still the same meeting.
  useOnValueChange(event?.uuid, () => {
    disableEditor();
    setOptimisticNotes(null);
  });
  useOnValueChange(isSuccess, () => {
    if (isSuccess) {
      disableEditor();
      closeLeavePageModal();
      if (tempTab) changeTab();
    }
  });
  const onSubmit = async values => {
    if (values.internal_notes?.html === notes) {
      disableEditor();
      return;
    }
    const internalNotes = values.internal_notes?.html === DEFAULT_VALUE ? '' : values.internal_notes?.html;
    track({
      name: TRACKING_EVENT_NAMES.MEETING_INTERACTION,
      properties: {
        action: 'edit_notes',
        location,
        event_uuid: event.uuid,
        mi_event_uuid: miEventUuid,
        is_calendly_meeting: isCalendlyMeeting(event)
      }
    });
    setOptimisticNotes(internalNotes);
    updateMeeting({
      id: event.id,
      attributes: {
        internal_notes: internalNotes
      }
    });
  };
  const onCancel = () => {
    disableEditor();
  };
  const onDiscard = () => {
    closeLeavePageModal();
    changeTab();
  };
  const onClose = () => {
    closeLeavePageModal();
    setTempTab('');
  };
  return <NotesFormWrapper>
      <MeetingNotesHeader showHeaderButtons={notes && !editorEnabled} notes={notes} onEdit={enableEditor} event={event} location={location} miEventUuid={miEventUuid} />
      <MeetingNotesBody notes={notes} showActionButtons={editorEnabled} onFocus={enableEditor} onCancel={onCancel} onSubmit={onSubmit} isLoading={isLoading} editorEnabled={editorEnabled} onDiscard={onDiscard} onClose={onClose} showLeavePageModal={showLeavePageModal} />
    </NotesFormWrapper>;
};

require("./meeting_notes_form.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meeting_notes_form.js");